import React, { useContext } from "react";
import { useDispatch } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { Icon, Link, Typography } from "@mui/material";
import Dropdown from "react-bootstrap/Dropdown";

import { logoutAction } from "@/features/auth/store/logoutAction";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { capitalizeEachWord, RpConfirmDialog } from "@/helpers/utils";
import ImageRP from "@/components/ImageRP";
import RpConfirm from "@/components/RpConfirm";
import { reportUserActivity } from "@/shared/helpers/userActivityHelper";
import { notifyOpenLogout } from "@/features/auth/helpers/logoutHelper";
import { clearRedirectPath } from "@/helpers/localStore";
import { ROUTES } from "@/helpers/routes/routePaths";

const PROFILE_IMAGE_STYLE = { width: 35, height: 35 };
const LOGOUT_STYLE = { cursor: "pointer" };
const SEPARATOR_STYLE = { fontSize: "x-small" };

type LoggedInUser = {
  email?: string;
  first_name?: string;
  id?: number | string;
  last_name?: string;
  profile_name?: string;
};

type ProfileContext = {
  loggedInUser?: LoggedInUser | null;
};

type Loader = (_isLoading: boolean) => void;
type DispatchAction = (_action: unknown) => unknown;
type ConfirmDialog = (
  _content: React.ReactNode,
  _onConfirm: () => Promise<void>
) => void;

const DropdownComponent = Dropdown as unknown as React.ComponentType<{
  alignRight?: boolean;
  children: React.ReactNode;
  className?: string;
}>;
const DropdownToggle = Dropdown.Toggle as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
  id?: string;
  variant?: string;
}>;
const DropdownMenu = Dropdown.Menu as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
}>;
const DropdownDivider = Dropdown.Divider as unknown as React.ComponentType;

const ProfileDropDown = () => {
  const dispatch = useDispatch() as DispatchAction;
  const navigate = useNavigate();
  const { loggedInUser } = useContext(GlobalContext) as ProfileContext;

  const loader = useContext(LoaderContext) as Loader;
  const { first_name, last_name, profile_name, email } = loggedInUser || {};

  /**
   * @method handleLogout
   * @description handle logout
   */
  const handleLogout = () => {
    (RpConfirmDialog as ConfirmDialog)(
      <RpConfirm
        message="Are your sure you want to logout?"
        heading="Logout"
      />,
      async () => {
        loader(true);
        try {
          await reportUserActivity("logout");
          await notifyOpenLogout(loggedInUser?.id);
          dispatch(logoutAction());
          navigate(ROUTES.ROOT, { replace: true });
          loader(false);
          if (typeof window !== "undefined") {
            window.location.reload();
          }
        } finally {
          loader(false);
          clearRedirectPath();
        }
      }
    );
  };

  return (
    <DropdownComponent className="user-profile align-content-center" alignRight>
      <DropdownToggle
        variant="default"
        id="dropdown-basic"
        className="pr-0 pr-lg-3"
      >
        <ImageRP
          src={profile_name}
          type="PROFILE"
          loaderBoxStyle={PROFILE_IMAGE_STYLE}
        />
        <Typography
          variant="subtitle2"
          className="user-detail text-truncate d-none d-lg-block mx-2"
        >
          {capitalizeEachWord(first_name)} {capitalizeEachWord(last_name)}
        </Typography>
      </DropdownToggle>
      <DropdownMenu className="py-0">
        <div className="p-3 d-flex align-items-center">
          <ImageRP
            src={profile_name}
            type="PROFILE"
            loaderBoxStyle={PROFILE_IMAGE_STYLE}
          />
          <div className="user-name text-truncate ml-2">
            <div className="heading8 text-truncate name">
              {capitalizeEachWord(first_name)} {capitalizeEachWord(last_name)}
            </div>

            <div className="text-truncate user-email user-select-all">
              {email}
            </div>
            <NavLink to={ROUTES.PROFILE} className="profile-link">
              <Icon fontSize="small">person_outline</Icon>
              My Profile
            </NavLink>
          </div>
        </div>
        <DropdownDivider />
        <div className="d-flex justify-content-between px-2 py-3">
          <button
            type="button"
            className="p-2 user-logout"
            onClick={handleLogout}
            style={LOGOUT_STYLE}
          >
            <Icon fontSize="small" className="mr-2" color="error">
              power_settings_new
            </Icon>
            Logout
          </button>
          <div className="profile-links">
            <Link
              href="https://www.hyperlocology.com/privacy-policy"
              className="profile-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </Link>
            <span style={SEPARATOR_STYLE}>|</span>
            <Link
              href="https://www.hyperlocology.com/cookie-policy"
              className="profile-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cookie Policy
            </Link>
          </div>
        </div>
      </DropdownMenu>
    </DropdownComponent>
  );
};

export default ProfileDropDown;
